import * as rd from '@devexperts/remote-data-ts'
import { PayloadAction } from '@reduxjs/toolkit'
import { DateTime } from 'luxon'
import { httpClient } from '@/lib/http/HttpClient.ts'
import { assertNever } from '@/lib/types/utils.ts'
import { BackendErrorModel } from '@/models/backendErrorModel.ts'
import { ResponseBase } from '@/models/http.ts'
import { BookingRefundPolicy, License, MainTimeSlot } from '@/models/license'
import { appSlice } from '@/store/appSlice.ts'
import { resetAllState } from '@/store/commonActions.ts'
import { RootState } from '@/store/configureStore.ts'
import { createAppSlice } from '@/store/createAppSlice.ts'
import { getLicenseFromUrl } from '@/utils/getLicenseFromUrl'
import { getSeasonEnd } from './utils/getSeasonEnd'

const DATE_FORMAT = 'dd-LL-yyyy'

interface LicenseString {
  /**
   * A unique id for the string (numeric value)
   */
  stringId: number

  /**
   * The key of the string
   */
  key: string

  /**
   * The language of the string
   */
  language: string

  /**
   * The license code of the string
   */
  licenseCode: string

  /**
   * The namespace of the strings
   */
  namespace: string

  /**
   * This value contains HTML tags
   */
  value: string
}

interface LicenseData {
  license: License
  isBookingEnabled: boolean
  seasonStartYear: number
  seasonEndYear: number
  seasonStart: string
  seasonEnd: string
  seasonalStart: string
  seasonalEnd: string
}

interface LicenseSliceState {
  license: rd.RemoteData<string, LicenseData>
  licenseCode: string
  mainTimeSlots: rd.RemoteData<string, MainTimeSlot[]>
  strings: rd.RemoteData<string, LicenseString[]>
}

const initialState: LicenseSliceState = {
  license: rd.initial,
  licenseCode: getLicenseFromUrl(),
  mainTimeSlots: rd.initial,
  strings: rd.initial,
}

export const licenseSlice = createAppSlice({
  extraReducers(builder) {
    builder.addCase(resetAllState, () => initialState)
  },
  initialState: initialState satisfies LicenseSliceState as LicenseSliceState,
  name: 'license',
  reducers: (create) => ({
    fetchLicenseStrings: create.asyncThunk<
      LicenseString[],
      void,
      { rejectValue: string }
    >(
      async (_, thunkApi): Promise<LicenseString[]> => {
        const rootState = thunkApi.getState() as RootState
        const license = licenseSlice.selectors.licenseCode(rootState)
        const country = appSlice.selectors.country(rootState)

        const response = await httpClient.fetch<
          ResponseBase<LicenseString[]>,
          BackendErrorModel
        >(`beaches/${license}/i18n/license-strings`, country)

        if (response.status === 'error') {
          throw thunkApi.rejectWithValue(
            'code' in response.error
              ? response.error.code
              : response.error.error
          )
        }
        return response.data.result
      },
      {
        fulfilled: (state, action) => {
          state.strings = rd.success(action.payload)
        },
        pending: (state) => {
          state.strings = rd.pending
        },
        rejected: (state, action) => {
          state.strings = rd.failure(action.payload ?? 'Unknown Error')
        },
      }
    ),
    loadLicense: create.asyncThunk<License, string, { rejectValue: null }>(
      async (licenseName, thunkApi): Promise<License> => {
        const rootState = thunkApi.getState() as RootState
        const country = appSlice.selectors.country(rootState)

        const resp = await httpClient.fetch<{ result: { license: License } }>(
          `beaches/${licenseName}?expand=beach,featureFlags`,
          country
        )

        switch (resp.status) {
          case 'success':
            return resp.data.result.license

          case 'error':
            throw thunkApi.rejectWithValue(null)

          default:
            assertNever(resp)
        }
      },
      {
        fulfilled: (state, action) => {
          const license = action.payload
          const now = DateTime.utc()
          let seasonStartYear = now.year
          let seasonEndYear = seasonStartYear
          const seasonEnd = getSeasonEnd(
            {
              bookingEndPeriod: license.bookingEndPeriod,
              bookingTimeLimit: license.bookingTimeLimit,
            },
            seasonStartYear
          )

          // New year enabled, current season not over yet and is september or later -> next year calendar
          if (license.bookingNextYear && now.month >= 9) {
            seasonEndYear++
            if (now.toMillis() > seasonEnd) {
              seasonStartYear++
            }
          }

          const isBookingEnabled = !!license.bookingStatus
          const seasonalStartDate = DateTime.fromFormat(
            `${license.seasonalStartDate}-${seasonEndYear}`,
            'dd-LL-yyyy'
          )

          state.license = rd.success({
            bookingMode: license.bookingMode,
            isBookingEnabled,
            license: {
              ...license,
              name: license.name,
            },
            seasonEnd: `${license.bookingEndPeriod}-${seasonEndYear}`,
            seasonEndYear,
            seasonStart: `${license.bookingStartPeriod}-${seasonStartYear}`,
            seasonStartYear,
            seasonalEnd: `${license.seasonalEndDate}-${seasonEndYear}`,
            seasonalStart: (now.toMillis() >= seasonalStartDate.toMillis()
              ? now.plus({ days: license.bookingToday ? 0 : 1 })
              : seasonalStartDate
            ).toFormat('dd-LL-yyyy'),
          })
        },
        options: {
          condition: (_, thunkApi) => {
            const state = thunkApi.getState() as RootState

            if (!rd.isInitial(state.license.license)) {
              return false
            }
          },
        },
        pending: (state) => {
          state.license = rd.pending
        },
        rejected: (state, action) => {
          state.license = rd.failure(action.payload ?? 'Unknown Error')
        },
      }
    ),
    loadMainTimeSlots: create.asyncThunk<
      MainTimeSlot[],
      string,
      { rejectValue: null }
    >(
      async (licenseName, thunkApi): Promise<MainTimeSlot[]> => {
        const rootState = thunkApi.getState() as RootState
        const country = appSlice.selectors.country(rootState)

        const resp = await httpClient.fetch<{
          result: { mainTimeSlots: MainTimeSlot[] }
        }>(`beaches/${licenseName}/main-time-slots`, country)

        switch (resp.status) {
          case 'success':
            return resp.data.result.mainTimeSlots

          case 'error':
            throw thunkApi.rejectWithValue(null)

          default:
            assertNever(resp)
        }
      },
      {
        fulfilled: (state, action) => {
          state.mainTimeSlots = rd.success(action.payload)
        },
        options: {
          condition: (_, thunkApi) => {
            const state = thunkApi.getState() as RootState

            if (!rd.isInitial(state.license.mainTimeSlots)) {
              return false
            }
          },
        },
        pending: (state) => {
          state.mainTimeSlots = rd.pending
        },
        rejected: (state, action) => {
          state.mainTimeSlots = rd.failure(action.payload ?? 'Unknown Error')
        },
      }
    ),
    setLicenseCode: create.reducer((state, action: PayloadAction<string>) => {
      state.licenseCode = action.payload
    }),
  }),
  selectors: {
    autoAfternoonReservation: getLicenseState(
      (state) => state.license.autoAfternoonReservation
    ),
    beachAddress: getLicenseState((state) => ({
      address: `${state.license.beach.address}, ${state.license.beach.city} (${state.license.beach.state})`,
      name: state.license.beach.name,
    })),
    beachCoordinates: getLicenseState((state) => ({
      lat: state.license.beach.lat,
      lng: state.license.beach.long,
    })),
    // TODO we should pick it from license, but currently we don't have this data
    beachTimeZone: () => 'Europe/Rome',
    bookingCartMode: getLicenseState((state) => state.license.bookingCartMode),
    bookingFiscalMode: getLicenseState(
      (state) => state.license.bookingFiscalInformationMode
    ),
    bookingPaymentIban: getLicenseState(
      (state) => state.license.bookingPaymentIban
    ),
    bookingUserDataNotes: getLicenseState(
      (state) => state.license.bookingUserDataNotes
    ),
    creditCardLimit: getLicenseState(
      (state) => state.license.bookingPaymentCreditcardLimit || Infinity
    ),
    isBookingEnabled: getLicenseState((state) => state.isBookingEnabled),
    isBookingPetFormVisible: getLicenseState(
      (state) => state.license.bookingPetFormVisible
    ),
    isBookingRefundable: getLicenseState(
      (state) =>
        !!state.license.bookingRefundPolicy &&
        state.license.bookingRefundPolicy !== BookingRefundPolicy.NEVER
    ),
    isDiscountCodeVisible: getLicenseState(
      (state) => !state.license.bookingDiscountCodeDisabled
    ),
    isTaxCodeRequired: getLicenseState(
      (state) => state.license.bookingTaxCodeRequired
    ),
    isWidgetWhiteLabelActive: getLicenseState(
      (state) => state.license.widgetWhiteLabelActive
    ),
    license: getLicenseState((state) => state.license),
    licenseCode: (state) => state.licenseCode,
    licenseName: getLicenseState((state) => state.license.name),
    licenseRemoteData: (state) => state.license,
    mainTimeSlots: (state) => state.mainTimeSlots,
    posthogEnabled: getLicenseState((state) => state.license.posthogEnabled),
    seasonEnd: getLicenseState((state) => state.seasonEnd),
    seasonStart: getLicenseState((state) => state.seasonStart),
    seasonStartYear: getLicenseState((state) => state.seasonStartYear),
    seasonalEnd: getLicenseState((state) => {
      const seasonEndDateTime = DateTime.fromFormat(
        state.seasonEnd,
        DATE_FORMAT
      )
      const seasonalEndDateTime = DateTime.fromFormat(
        state.seasonalEnd,
        DATE_FORMAT
      )

      if (seasonalEndDateTime > seasonEndDateTime) {
        return state.seasonEnd
      }

      return state.seasonalEnd
    }),
    seasonalStart: getLicenseState((state) => state.seasonalStart),
    strings: (state) => state.strings,
    stringsValue: (state) =>
      rd.isSuccess(state.strings) ? state.strings.value : [],
  },
})
type GetLicenseStateCallback<T> = (licenseData: LicenseData) => T

function getLicenseState<T>(callback: GetLicenseStateCallback<T>) {
  return (state: LicenseSliceState) =>
    rd.isSuccess(state.license) ? callback(state.license.value) : null
}
